import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import reportsService from "services/reportsService";

interface ReportViewerProps {
  reportPath: string;
  parameters?: Record<string, string>;
  format?: string;
  title?: string;
  height?: string | number;
}

const ReportViewer = ({
  reportPath,
  parameters,
  format = "PDF",
  title,
  height = "calc(100vh - 96px)",
}: ReportViewerProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const paramsKey = useMemo(() => JSON.stringify(parameters ?? {}), [parameters]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    setLoading(true);
    setError(null);
    setBlobUrl(null);

    reportsService
      .renderReport({
        reportPath,
        format,
        parameters: Object.fromEntries(Object.entries(parameters ?? {}).map(([k, v]) => [k, [v]])),
      })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch(async (e) => {
        if (cancelled) return;
        const blob = e?.response?.data as Blob | undefined;
        let detail: string | undefined;
        if (blob && typeof blob.text === "function") {
          try {
            const text = await blob.text();
            try {
              detail = (JSON.parse(text)?.error as string) ?? text;
            } catch {
              detail = text;
            }
          } catch {
            // ignore blob read failures
          }
        }
        setError(detail || e?.response?.statusText || e?.message || "Failed to load report.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [reportPath, format, paramsKey]);

  return (
    <Box display="flex" flexDirection="column" height={height}>
      <Box display="flex" alignItems="center" px={2} py={1}>
        <Typography variant="h6">{title ?? lastSegment(reportPath)}</Typography>
      </Box>
      <Box flex={1} position="relative" sx={{ border: 1, borderColor: "divider", mx: 2, mb: 2 }}>
        {loading && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Box>
        )}
        {error && !loading && (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" px={3}>
            <Typography color="error">Could not load report: {error}</Typography>
          </Box>
        )}
        {blobUrl && !error && (
          <iframe
            src={blobUrl}
            title={title ?? lastSegment(reportPath)}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        )}
      </Box>
    </Box>
  );
};

const lastSegment = (path: string) => path.replace(/\/+$/, "").split("/").pop() ?? "Report";

export default ReportViewer;
